/**
 * FlowScript AST.
 *
 * Every node is a discriminated union member keyed on `kind`, carries the
 * source `range` it was parsed from, and carries a `stableId` derived from its
 * structural path (see `src/language/ast/ids.ts`). The stable id is what keeps
 * the diagram from being torn down and rebuilt on every keystroke.
 *
 * Labels keep the user's original text verbatim — capitalisation, punctuation
 * and Unicode all survive. Only *keywords* are matched case-insensitively, and
 * that normalisation happens in the tokenizer, never on label text.
 */
import type { SourceRange } from './source';

/* ------------------------------------------------------------------ *
 * Metadata / annotations
 * ------------------------------------------------------------------ */

/** Visual shape a node may be forced into via `@shape(...)`. */
export type MetadataShape = 'process' | 'decision' | 'io' | 'subprocess' | 'terminator' | 'note';

export const METADATA_SHAPES: readonly MetadataShape[] = [
  'process',
  'decision',
  'io',
  'subprocess',
  'terminator',
  'note',
];

/** A single `@name("value", ...)` annotation attached to the statement below it. */
export interface Annotation {
  readonly kind: 'Annotation';
  /** Lower-cased annotation name, e.g. `color`. */
  readonly name: string;
  /** Name exactly as written, for diagnostics. */
  readonly rawName: string;
  /** Argument values, already unescaped from their string literals. */
  readonly args: readonly string[];
  readonly range: SourceRange;
}

/**
 * Annotation values after validation. Anything that failed validation is
 * dropped here and reported as a diagnostic instead — this type only ever
 * holds values that are safe to render.
 */
export interface NodeMetadata {
  /** `#rgb` / `#rrggbb`, already validated. */
  readonly color?: string;
  /** Icon slug from the built-in set; unknown slugs are rejected. */
  readonly icon?: string;
  /** Forces the visual shape. */
  readonly shape?: MetadataShape;
  /** Explicit, user-chosen node id. Must be unique across the document. */
  readonly id?: string;
  /** `http:` / `https:` / `mailto:` URL. Other schemes are rejected. */
  readonly link?: string;
  /** Free-text note rendered in the inspector and as a tooltip. */
  readonly note?: string;
  /** Space-separated safe class tokens (`[A-Za-z0-9_-]`), prefixed on render. */
  readonly className?: string;
}

export const EMPTY_METADATA: NodeMetadata = Object.freeze({});

/* ------------------------------------------------------------------ *
 * Base
 * ------------------------------------------------------------------ */

export interface NodeBase {
  /** Source span this node was parsed from. */
  readonly range: SourceRange;
  /**
   * Structural identity, stable across edits that do not change the node's
   * position in the tree. Assigned by `assignStableIds`.
   */
  readonly stableId: string;
  /** Validated metadata from `@...` annotations preceding this node. */
  readonly metadata: NodeMetadata;
  /** Raw annotations, kept for round-tripping through the formatter. */
  readonly annotations: readonly Annotation[];
}

/** A label: user text, kept verbatim, plus where it came from. */
export interface Label {
  readonly text: string;
  readonly range: SourceRange;
}

/* ------------------------------------------------------------------ *
 * Statements
 * ------------------------------------------------------------------ */

export interface Block {
  readonly kind: 'Block';
  readonly statements: readonly Statement[];
  readonly range: SourceRange;
  readonly stableId: string;
}

/** `Do the thing;` — the default when no keyword matches. */
export interface ActionStatement extends NodeBase {
  readonly kind: 'ActionStatement';
  readonly label: Label;
}

/** `start;` / `start Application;` / `Start app;` (keyword-initial phrase). */
export interface StartStatement extends NodeBase {
  readonly kind: 'StartStatement';
  /** Text after the keyword; `null` for a bare `start;`. */
  readonly label: Label | null;
  /** The keyword exactly as written, so `Start` does not become `start`. */
  readonly keyword: string;
}

/** `end;` / `end Application;` / `End the diagram;`. */
export interface EndStatement extends NodeBase {
  readonly kind: 'EndStatement';
  readonly label: Label | null;
  readonly keyword: string;
}

/**
 * How a terminator's text becomes a node label.
 *
 * The brief describes both readings: the worked example shows `Start app;`
 * producing a node captioned "Start app", while the language rules say the
 * label is "the text after the keyword". Both are supported.
 *
 * - `full-phrase` (default) — the whole statement, exactly as typed:
 *   `Start app;` -> "Start app". What you write is what you see.
 * - `after-keyword` — only the remainder: `start Application;` -> "Application".
 *
 * Either way a bare `start;` / `end;` yields "Start" / "End".
 */
export type TerminatorLabelStyle = 'full-phrase' | 'after-keyword';

export function terminatorLabel(
  statement: StartStatement | EndStatement,
  style: TerminatorLabelStyle = 'full-phrase',
): string {
  const fallback = statement.kind === 'StartStatement' ? 'Start' : 'End';
  if (!statement.label || statement.label.text === '') return fallback;
  if (style === 'after-keyword') return statement.label.text;
  return `${statement.keyword} ${statement.label.text}`;
}

/** Custom branch labels: `if (C?) yes "Approved" no "Rejected"`. */
export interface BranchLabels {
  readonly yes: Label | null;
  readonly no: Label | null;
}

export interface IfStatement extends NodeBase {
  readonly kind: 'IfStatement';
  readonly condition: Label;
  readonly branchLabels: BranchLabels;
  readonly consequent: Block;
  /**
   * `Block` for `else { ... }`, `IfStatement` for `else if (...)`, `null` when
   * there is no else. Chaining through `IfStatement` is what makes `else if`
   * compile into a readable decision chain rather than a nested block.
   */
  readonly alternate: Block | IfStatement | null;
  /** True when this node came from an `else if` (affects formatter output only). */
  readonly isElseIf: boolean;
}

export interface WhileStatement extends NodeBase {
  readonly kind: 'WhileStatement';
  readonly condition: Label;
  readonly branchLabels: BranchLabels;
  readonly body: Block;
}

/** `repeat { ... } until (C?);` — body runs at least once. */
export interface RepeatStatement extends NodeBase {
  readonly kind: 'RepeatStatement';
  readonly condition: Label;
  readonly branchLabels: BranchLabels;
  readonly body: Block;
}

/** `for each (Item in collection) { ... }`. */
export interface ForEachStatement extends NodeBase {
  readonly kind: 'ForEachStatement';
  /** Whole header text, e.g. `Item in collection`. */
  readonly header: Label;
  /** `Item`, when the header parsed as `<item> in <collection>`; else `null`. */
  readonly item: Label | null;
  /** `collection`, under the same condition. */
  readonly collection: Label | null;
  readonly branchLabels: BranchLabels;
  readonly body: Block;
}

export interface SwitchCase {
  readonly kind: 'SwitchCase';
  /** `null` for the `default:` clause. */
  readonly test: Label | null;
  readonly body: Block;
  readonly range: SourceRange;
  readonly stableId: string;
  readonly metadata: NodeMetadata;
  readonly annotations: readonly Annotation[];
}

export interface SwitchStatement extends NodeBase {
  readonly kind: 'SwitchStatement';
  readonly discriminant: Label;
  readonly cases: readonly SwitchCase[];
}

export interface CallArgument {
  readonly text: string;
  readonly range: SourceRange;
}

/** `call greet;` / `call validateUser(currentUser);` */
export interface CallStatement extends NodeBase {
  readonly kind: 'CallStatement';
  readonly callee: Label;
  /** `null` when written without parentheses at all. */
  readonly args: readonly CallArgument[] | null;
}

export interface ReturnStatement extends NodeBase {
  readonly kind: 'ReturnStatement';
  readonly label: Label | null;
}

export interface BreakStatement extends NodeBase {
  readonly kind: 'BreakStatement';
}

export interface ContinueStatement extends NodeBase {
  readonly kind: 'ContinueStatement';
}

/**
 * Placeholder emitted by error recovery so a broken region still occupies a
 * slot in the tree. Compiles to an `error` graph node rather than vanishing,
 * which keeps surrounding node ids stable while the user is mid-edit.
 */
export interface ErrorStatement extends NodeBase {
  readonly kind: 'ErrorStatement';
  /** The text the parser could not make sense of. */
  readonly text: string;
}

export type Statement =
  | ActionStatement
  | StartStatement
  | EndStatement
  | IfStatement
  | WhileStatement
  | RepeatStatement
  | ForEachStatement
  | SwitchStatement
  | CallStatement
  | ReturnStatement
  | BreakStatement
  | ContinueStatement
  | ErrorStatement;

export type StatementKind = Statement['kind'];

/* ------------------------------------------------------------------ *
 * Declarations & program
 * ------------------------------------------------------------------ */

export interface Parameter {
  readonly name: string;
  readonly range: SourceRange;
}

export interface FunctionDeclaration extends NodeBase {
  readonly kind: 'FunctionDeclaration';
  readonly name: Label;
  /** Lower-cased name; function lookup is case-insensitive. */
  readonly normalizedName: string;
  /** `null` when declared without parentheses. Purely informational. */
  readonly parameters: readonly Parameter[] | null;
  readonly body: Block;
}

export type TopLevelStatement = FunctionDeclaration | Statement;

export interface Program {
  readonly kind: 'Program';
  readonly body: readonly TopLevelStatement[];
  readonly range: SourceRange;
  readonly stableId: string;
}

export type AstNode = Program | FunctionDeclaration | Block | Statement | SwitchCase | Annotation;

/* ------------------------------------------------------------------ *
 * Guards & traversal
 * ------------------------------------------------------------------ */

export function isFunctionDeclaration(node: TopLevelStatement): node is FunctionDeclaration {
  return node.kind === 'FunctionDeclaration';
}

export function isStatement(node: TopLevelStatement): node is Statement {
  return node.kind !== 'FunctionDeclaration';
}

/** Statements that unconditionally leave the current flow. */
export function isTerminalStatementKind(kind: StatementKind): boolean {
  return (
    kind === 'EndStatement' ||
    kind === 'ReturnStatement' ||
    kind === 'BreakStatement' ||
    kind === 'ContinueStatement'
  );
}

/** Direct child blocks of a statement, in source order. */
export function childBlocks(statement: Statement): readonly Block[] {
  switch (statement.kind) {
    case 'IfStatement': {
      const blocks: Block[] = [statement.consequent];
      if (statement.alternate && statement.alternate.kind === 'Block') {
        blocks.push(statement.alternate);
      }
      return blocks;
    }
    case 'WhileStatement':
    case 'RepeatStatement':
    case 'ForEachStatement':
      return [statement.body];
    case 'SwitchStatement':
      return statement.cases.map((c) => c.body);
    default:
      return [];
  }
}

/**
 * Depth-first walk over every statement in the program, including nested
 * blocks, `else if` chains, switch cases and function bodies.
 */
export function* walkStatements(program: Program): Generator<Statement> {
  for (const top of program.body) {
    if (top.kind === 'FunctionDeclaration') {
      yield* walkBlock(top.body);
    } else {
      yield* walkStatement(top);
    }
  }
}

export function* walkBlock(block: Block): Generator<Statement> {
  for (const statement of block.statements) {
    yield* walkStatement(statement);
  }
}

export function* walkStatement(statement: Statement): Generator<Statement> {
  yield statement;
  for (const block of childBlocks(statement)) {
    yield* walkBlock(block);
  }
  if (statement.kind === 'IfStatement' && statement.alternate?.kind === 'IfStatement') {
    yield* walkStatement(statement.alternate);
  }
}

/** Every function declared in the program, in source order. */
export function collectFunctions(program: Program): readonly FunctionDeclaration[] {
  return program.body.filter(isFunctionDeclaration);
}

/** Top-level statements that are not function declarations — the main flow. */
export function mainFlow(program: Program): readonly Statement[] {
  return program.body.filter(isStatement);
}
